// Media kit assets data structure
export interface MediaAsset {
  id: string;
  name: string;
  category: 'logo' | 'icon' | 'screenshot' | 'feature' | 'brand' | 'other';
  type: 'png' | 'jpg' | 'svg' | 'ico';
  variants: AssetVariant[];
  description: string;
  usage: string;
  tags: string[];
}

export interface AssetVariant {
  id: string;
  name: string;
  path: string;
  size?: string;
  dimensions?: string;
  background?: 'transparent' | 'white' | 'dark' | 'color';
  format: 'png' | 'jpg' | 'svg' | 'ico';
  downloadPath: string;
}

export const mediaAssets: MediaAsset[] = [
  {
    id: 'logo-primary',
    name: 'Newsroom Logo - Primary',
    category: 'logo',
    type: 'png',
    description: 'Primary Newsroom logo for general use',
    usage: 'Use for main branding, headers, and primary brand placement',
    tags: ['primary', 'main', 'brand', 'wordmark'],
    variants: [
      {
        id: 'logo-light-large',
        name: 'Light - Large',
        path: '/logo-light.png',
        size: '425KB',
        dimensions: '2000x800px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-light.png'
      },
      {
        id: 'logo-light-medium',
        name: 'Light - Medium',
        path: '/logo-lightMiddle.png',
        size: '92KB',
        dimensions: '1000x400px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-lightMiddle.png'
      },
      {
        id: 'logo-light-small',
        name: 'Light - Small',
        path: '/logo-lightmini.png',
        size: '7KB',
        dimensions: '300x120px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-lightmini.png'
      }
    ]
  },
  {
    id: 'logo-dark',
    name: 'Newsroom Logo - Dark Theme',
    category: 'logo',
    type: 'png',
    description: 'Dark version of Newsroom logo for light backgrounds',
    usage: 'Use on light backgrounds, white papers, and bright interfaces',
    tags: ['dark', 'light background', 'contrast'],
    variants: [
      {
        id: 'logo-dark-large',
        name: 'Dark - Large',
        path: '/logo-dark.png',
        size: '423KB',
        dimensions: '2000x800px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-dark.png'
      },
      {
        id: 'logo-dark-medium',
        name: 'Dark - Medium',
        path: '/logo-darkMiddle.png',
        size: '69KB',
        dimensions: '1000x400px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-darkMiddle.png'
      },
      {
        id: 'logo-dark-small',
        name: 'Dark - Small',
        path: '/logo-darkmini.png',
        size: '11KB',
        dimensions: '300x120px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-darkmini.png'
      }
    ]
  },
  {
    id: 'logo-variations',
    name: 'Logo Variations',
    category: 'logo',
    type: 'png',
    description: 'Alternative logo variations and styles',
    usage: 'Special use cases, alternative layouts, and style variations',
    tags: ['variation', 'alternative', 'style'],
    variants: [
      {
        id: 'logo-dark2',
        name: 'Dark Style 2',
        path: '/logo-dark2.png',
        size: '64KB',
        dimensions: '800x320px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-dark2.png'
      },
      {
        id: 'logo-darko',
        name: 'Dark Outlined',
        path: '/logo-darko.png',
        size: '55KB',
        dimensions: '800x320px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-darko.png'
      },
      {
        id: 'logo-darkrr',
        name: 'Dark Rounded',
        path: '/logo-darkrr.png',
        size: '47KB',
        dimensions: '800x320px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo-darkrr.png'
      }
    ]
  },
  {
    id: 'app-icons',
    name: 'App Icons',
    category: 'icon',
    type: 'png',
    description: 'Application icons for various platforms and uses',
    usage: 'App stores, shortcuts, favicons, and platform-specific icons',
    tags: ['app', 'icon', 'favicon', 'mobile'],
    variants: [
      {
        id: 'logo192',
        name: 'App Icon 192px',
        path: '/logo192.png',
        size: '8KB',
        dimensions: '192x192px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo192.png'
      },
      {
        id: 'logo512',
        name: 'App Icon 512px',
        path: '/logo512.png',
        size: '22KB',
        dimensions: '512x512px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/logo512.png'
      },
      {
        id: 'apple-touch-icon',
        name: 'Apple Touch Icon',
        path: '/apple-touch-icon.png',
        size: '11KB',
        dimensions: '180x180px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/apple-touch-icon.png'
      }
    ]
  },
  {
    id: 'favicons',
    name: 'Favicons',
    category: 'icon',
    type: 'ico',
    description: 'Website favicons in multiple formats and sizes',
    usage: 'Browser tabs, bookmarks, and website identification',
    tags: ['favicon', 'browser', 'bookmark'],
    variants: [
      {
        id: 'favicon-ico',
        name: 'Favicon ICO',
        path: '/favicon.ico',
        size: '15KB',
        dimensions: 'Multi-size',
        background: 'transparent',
        format: 'ico',
        downloadPath: '/favicon.ico'
      },
      {
        id: 'favicon-16',
        name: 'Favicon 16px',
        path: '/favicon-16x16.png',
        size: '1KB',
        dimensions: '16x16px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/favicon-16x16.png'
      },
      {
        id: 'favicon-32',
        name: 'Favicon 32px',
        path: '/favicon-32x32.png',
        size: '1KB',
        dimensions: '32x32px',
        background: 'transparent',
        format: 'png',
        downloadPath: '/favicon-32x32.png'
      }
    ]
  },
  {
    id: 'feature-images',
    name: 'Feature Showcase',
    category: 'feature',
    type: 'jpg',
    description: 'High-quality feature demonstration images',
    usage: 'Marketing materials, presentations, and feature highlights',
    tags: ['features', 'showcase', 'demo', 'marketing'],
    variants: [
      {
        id: 'feature-intelligence',
        name: 'AI Intelligence Features',
        path: '/feature-intelligence.jpg',
        size: '496KB',
        dimensions: '1920x1080px',
        background: 'color',
        format: 'jpg',
        downloadPath: '/feature-intelligence.jpg'
      },
      {
        id: 'feature-performance',
        name: 'Performance Analytics',
        path: '/feature-performance.jpg',
        size: '961KB',
        dimensions: '1920x1080px',
        background: 'color',
        format: 'jpg',
        downloadPath: '/feature-performance.jpg'
      },
      {
        id: 'feature-visualization',
        name: 'Data Visualization',
        path: '/feature-visualization.jpg',
        size: '694KB',
        dimensions: '1920x1080px',
        background: 'color',
        format: 'jpg',
        downloadPath: '/feature-visualization.jpg'
      }
    ]
  },
  {
    id: 'brand-elements',
    name: 'Brand Elements',
    category: 'brand',
    type: 'svg',
    description: 'Vector brand elements and supplementary graphics',
    usage: 'Design systems, illustrations, and brand consistency',
    tags: ['brand', 'vector', 'elements', 'svg'],
    variants: [
      {
        id: 'claude-color',
        name: 'Claude AI Symbol',
        path: '/claude-color.svg',
        size: '2KB',
        dimensions: 'Vector',
        background: 'transparent',
        format: 'svg',
        downloadPath: '/claude-color.svg'
      },
      {
        id: 'market-chat',
        name: 'Market Chat Icon',
        path: '/market-chat.svg',
        size: '3KB',
        dimensions: 'Vector',
        background: 'transparent',
        format: 'svg',
        downloadPath: '/market-chat.svg'
      },
      {
        id: 'smartcharts',
        name: 'Smart Charts Icon',
        path: '/smartcharts.svg',
        size: '4KB',
        dimensions: 'Vector',
        background: 'transparent',
        format: 'svg',
        downloadPath: '/smartcharts.svg'
      }
    ]
  },
  {
    id: 'social-media',
    name: 'Social Media Assets',
    category: 'brand',
    type: 'png',
    description: 'Optimized assets for social media platforms',
    usage: 'Social media posts, profiles, and sharing',
    tags: ['social', 'sharing', 'og', 'twitter'],
    variants: [
      {
        id: 'og-image',
        name: 'Open Graph Image',
        path: '/og-image.png',
        size: '185KB',
        dimensions: '1200x630px',
        background: 'color',
        format: 'png',
        downloadPath: '/og-image.png'
      }
    ]
  }
];

// Helper functions
export const getAssetsByCategory = (category: string) => {
  return mediaAssets.filter(asset => asset.category === category);
};

export const getAssetById = (id: string) => {
  return mediaAssets.find(asset => asset.id === id);
};

export const getAllVariants = () => {
  return mediaAssets.flatMap(asset => asset.variants);
};

export const getVariantsByFormat = (format: string) => {
  return mediaAssets.flatMap(asset => 
    asset.variants.filter(variant => variant.format === format)
  );
};
